import axios from 'axios';

const GRAPHQL_URL = process.env.REACT_APP_API_NEWS || '';
const API_HOST = GRAPHQL_URL.replace('https://', '').replace('/graphql', '');
const REALTIME_URL =
  process.env.REACT_APP_REALTIME_NEWS ||
  ('wss://' + API_HOST.replace('appsync-api', 'appsync-realtime-api') + '/graphql');

const GET_NEWS = `
  query GetNews($limit: Int, $nextToken: String) {
    GetNews(limit: $limit, nextToken: $nextToken) {
      news { id name description createdAt }
      nextToken
    }
  }
`;

const ON_CREATE_NEW = `
  subscription OnCreateNew {
    onCreateNew { id name description createdAt }
  }
`;

// Paginates through all pages (100 items per request) and returns every news item.
const obtenerNews = async (token) => {
  let allNews = [];
  let nextToken = null;

  do {
    const { data } = await axios.post(
      GRAPHQL_URL,
      { query: GET_NEWS, variables: { limit: 100, nextToken } },
      { headers: { Authorization: token, 'Content-Type': 'application/json' } }
    );
    if (data?.errors?.length) throw new Error(data.errors[0].message);
    const result = data?.data?.GetNews;
    allNews = allNews.concat(result?.news ?? []);
    nextToken = result?.nextToken ?? null;
  } while (nextToken);

  return allNews;
};

// Opens an AppSync real-time WebSocket subscription with Cognito auth.
// Reconnects automatically on unexpected disconnects (exponential backoff, up to 5 retries).
// Returns an unsubscribe function — call it on component unmount.
const suscribirseANews = (token, onData, onError) => {
  let ws = null;
  let stopped = false;
  let retryCount = 0;
  const MAX_RETRIES = 5;

  const connect = () => {
    if (stopped) return;

    const authHeader = { Authorization: token, host: API_HOST };
    const b64Header = btoa(JSON.stringify(authHeader));

    ws = new WebSocket(
      `${REALTIME_URL}?header=${b64Header}&payload=e30=`,
      'graphql-ws'
    );

    ws.onopen = () => {
      retryCount = 0;
      ws.send(JSON.stringify({ type: 'connection_init' }));
    };

    ws.onmessage = ({ data }) => {
      let msg;
      try {
        msg = JSON.parse(data);
      } catch (_) {
        return;
      }

      switch (msg.type) {
        case 'connection_ack':
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({
              id: 'sub-news',
              type: 'start',
              payload: {
                data: JSON.stringify({ query: ON_CREATE_NEW, variables: {} }),
                extensions: { authorization: authHeader },
              },
            }));
          }
          break;

        case 'ka':
          // AppSync keep-alive — no response required
          break;

        case 'start_ack':
          // Subscription registered successfully
          break;

        case 'data':
          if (msg.id === 'sub-news' && msg.payload?.data?.onCreateNew) {
            onData(msg.payload.data.onCreateNew);
          }
          break;

        case 'connection_error':
        case 'error':
          onError?.(msg.payload);
          break;

        default:
          break;
      }
    };

    ws.onerror = (err) => onError?.(err);

    ws.onclose = (event) => {
      if (stopped) return;
      // Do not retry on auth/bad-request errors — a new token is needed
      if (event.code === 1000 || event.code === 4400 || event.code === 4401 || event.code === 4403) return;

      if (retryCount < MAX_RETRIES) {
        retryCount++;
        const delay = Math.min(500 * 2 ** retryCount, 30000);
        setTimeout(connect, delay);
      } else {
        onError?.({ message: 'WebSocket: max reconnection attempts reached' });
      }
    };
  };

  connect();

  return () => {
    stopped = true;
    try {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ id: 'sub-news', type: 'stop' }));
      }
      ws?.close(1000);
    } catch (_) {}
  };
};

const newsService = { obtenerNews, suscribirseANews };
export default newsService;
